import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import Button from "../components/button";
import ThemeToggle from "../components/theme-toggle";
import appIcon from "../static/icon.png";
import phonePreview from "../static/website.png";
import trackScreenshot from "../static/deliveries-tracker/track.jpg";
import mapScreenshot from "../static/deliveries-tracker/map.jpg";
import notificationsScreenshot from "../static/deliveries-tracker/notifications.jpg";
import detailsScreenshot from "../static/deliveries-tracker/details.jpg";
import widgetsScreenshot from "../static/deliveries-tracker/widgets.jpg";
import appIntelligenceScreenshot from "../static/deliveries-tracker/app-intelligence.jpg";
import darkScreenshot from "../static/deliveries-tracker/dark.jpg";
import { IOS_URL, SUPPORT_EMAIL, TWITTER_URL } from "../constants/data";

const YOUTUBE_URL = "https://www.youtube.com/@deliveriestrackerapphq";

const features = [
  {
    label: "Track",
    title: "Track all your packages",
    description:
      "Keep FedEx, USPS, UPS, OnTrac, DHL, and other major carriers organized in one timeline.",
    image: trackScreenshot,
    imageAlt: "Track view showing multiple package cards from different carriers.",
  },
  {
    label: "Map",
    title: "Real-time package location",
    description:
      "Open the map view and check where your package is when location data is available.",
    image: mapScreenshot,
    imageAlt: "Map view showing a package route and current package location.",
  },
  {
    label: "Notifications",
    title: "Never miss a delivery",
    description:
      "Receive lock-screen updates when your shipment changes status or reaches a new checkpoint.",
    image: notificationsScreenshot,
    imageAlt: "Notification screen with delivery updates on the lock screen.",
  },
  {
    label: "Details",
    title: "Detailed tracking history",
    description:
      "See last location, status changes, scheduled dates, and the full movement history for every item.",
    image: detailsScreenshot,
    imageAlt: "Details screen with tracking history, map, and status updates.",
  },
  {
    label: "Widgets",
    title: "Track from the home screen",
    description:
      "Follow key deliveries from your widget stack without opening the app every time.",
    image: widgetsScreenshot,
    imageAlt: "Home screen widgets displaying package delivery states.",
  },
  {
    label: "App Intelligence",
    title: "Estimated delivery times",
    description:
      "Get delivery-time estimates based on route and carrier behavior when app intelligence is available.",
    image: appIntelligenceScreenshot,
    imageAlt: "App intelligence screen with predicted delivery times and chart data.",
  },
  {
    label: "Themes",
    title: "Dark and light modes",
    description:
      "Choose the look that fits your setup with both dark and light themes available.",
    image: darkScreenshot,
    imageAlt: "Dark mode tracking screen shown on an iPhone.",
  },
];

export default function Home({ isDarkTheme, onToggleTheme }) {
  return (
    <Page>
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      <Shell>
        <TopBar>
          <Brand>
            <BrandMark src={appIcon} alt="" aria-hidden="true" />
            <BrandText>
              <strong>Deliveries Tracker</strong>
              <span>Simple parcel tracking for Apple devices</span>
            </BrandText>
          </Brand>
          <Nav aria-label="Primary">
            <NavLink href={`mailto:${SUPPORT_EMAIL}?subject=Support`}>
              Contact
            </NavLink>
            <NavLink
              href={YOUTUBE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              YouTube
            </NavLink>
            <StyledLink to="/privacy-policy">Privacy Policy</StyledLink>
            <ThemeToggle checked={isDarkTheme} onClick={onToggleTheme} />
          </Nav>
        </TopBar>

        <Hero id="main-content">
          <HeroCopy>
            <Headline>Track packages, maps, alerts, widgets, and themes in one app.</Headline>
            <Lead>
              Stay on top of every delivery with a clear tracking timeline,
              real-time map views when available, timely notifications,
              detailed shipment history, home screen widgets, smart delivery
              estimates, and a polished experience in both dark and light
              modes.
            </Lead>
            <ActionRow>
              <Button href={IOS_URL} disableHoverLift disableShadow>
                <AppStoreLabel>
                  <AppStoreIcon
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"
                    />
                  </AppStoreIcon>
                  <span>Download on the App Store</span>
                </AppStoreLabel>
              </Button>
            </ActionRow>
          </HeroCopy>

          <HeroVisual>
            <PreviewCard>
              <PreviewGlow />
              <PreviewTitle>Built for iPhone and iPad</PreviewTitle>
              <PreviewText>
                Keep your deliveries in sync with iCloud across iPhone and
                iPad, so your tracking list, updates, and delivery details stay
                with you everywhere.
              </PreviewText>
              <PreviewFrame>
                <PreviewImage
                  src={phonePreview}
                  alt="Preview of the Deliveries Tracker app on iPhone and iPad"
                />
              </PreviewFrame>
            </PreviewCard>
          </HeroVisual>
        </Hero>

        <Section>
          <FeatureGrid aria-label="Deliveries Tracker features">
            {features.map((feature) => (
              <FeatureCard key={feature.title}>
                <FeatureFigure>
                  <FeatureImage src={feature.image} alt={feature.imageAlt} />
                </FeatureFigure>
                <FeatureBody>
                  <FeatureLabel>{feature.label}</FeatureLabel>
                  <FeatureTitle>{feature.title}</FeatureTitle>
                  <FeatureDescription>{feature.description}</FeatureDescription>
                </FeatureBody>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </Section>

        <BottomPanel>
          <BottomCopy>
            <SupportEyebrow>Support</SupportEyebrow>
            <BottomTitle>Questions, feedback, or a carrier you need?</BottomTitle>
            <BottomText>
              Reach out directly or follow us for product updates.
            </BottomText>
          </BottomCopy>
          <BottomActions>
            <Button
              href={`mailto:${SUPPORT_EMAIL}?subject=Support`}
              variant="secondary"
              target="_self"
              rel=""
            >
              Email us
            </Button>
            <Button href={TWITTER_URL} variant="secondary">
              Follow us
            </Button>
          </BottomActions>
        </BottomPanel>
      </Shell>
    </Page>
  );
}

const Page = styled.main`
  padding: 32px 20px 60px;

  @media only screen and (max-width: ${({ theme }) => theme.mobileBreakpoint}) {
    padding: 20px 16px 48px;
  }
`;

const SkipLink = styled.a`
  position: absolute;
  left: 20px;
  top: 12px;
  z-index: 10;
  padding: 0.8rem 1rem;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.navy};
  color: #fff;
  transform: translateY(-140%);
  transition: transform 0.2s ease;

  &:focus {
    transform: translateY(0);
  }
`;

const Shell = styled.div`
  max-width: 1180px;
  margin: 0 auto;
`;

const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 40px;

  @media only screen and (max-width: ${({ theme }) => theme.mobileBreakpoint}) {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 28px;
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
`;

const BrandMark = styled.img`
  width: 68px;
  height: 68px;
  border-radius: 20px;
  box-shadow: 0 16px 30px rgba(22, 52, 64, 0.18);
  object-fit: cover;
`;

const BrandText = styled.div`
  display: flex;
  flex-direction: column;

  strong {
    font-size: 1.6rem;
    line-height: 1.05;
  }

  span {
    color: ${({ theme }) => theme.colors.muted};
    font-size: 1.15rem;
    line-height: 1.3;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 18px;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 1rem;
`;

const NavLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0.75rem 1rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.navSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-weight: 700;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    background: ${({ theme }) => theme.colors.cardSurface};
    border-color: ${({ theme }) => theme.colors.accentDark};
  }

  &:focus-visible {
    transform: translateY(-1px);
  }
`;

const StyledLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0.75rem 1rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.navSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-weight: 700;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    background: ${({ theme }) => theme.colors.cardSurface};
    border-color: ${({ theme }) => theme.colors.accentDark};
  }

  &:focus-visible {
    transform: translateY(-1px);
  }
`;

const Hero = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(320px, 0.9fr);
  gap: 36px;
  align-items: center;
  padding: 28px 0 68px;

  @media only screen and (max-width: ${({ theme }) => theme.mobileBreakpoint}) {
    grid-template-columns: 1fr;
    padding: 12px 0 52px;
  }
`;

const HeroCopy = styled.div`
  max-width: 620px;
`;

const Headline = styled.h1`
  margin: 0 0 18px;
  font-size: clamp(2.3rem, 4.6vw, 4.2rem);
  line-height: 1.02;
  letter-spacing: -0.05em;

  @media only screen and (max-width: ${({ theme }) => theme.mobileBreakpoint}) {
    font-size: clamp(2rem, 8vw, 3rem);
  }
`;

const Lead = styled.p`
  margin: 0;
  max-width: 560px;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 1.08rem;
  line-height: 1.8;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 28px;
`;

const AppStoreLabel = styled.span`
  display: inline-flex;
  align-items: center;
  flex-direction: row-reverse;
  gap: 0.65rem;
`;

const AppStoreIcon = styled.svg`
  width: 1.15rem;
  height: 1.15rem;
  flex: 0 0 auto;
`;

const HeroVisual = styled.div`
  display: flex;
  justify-content: center;
`;

const PreviewCard = styled.div`
  position: relative;
  width: min(100%, 500px);
  padding: 28px;
  border-radius: 32px;
  background: ${({ theme }) => theme.colors.cardSurface};
  border: 1px solid ${({ theme }) => theme.colors.cardBorderStrong};
  box-shadow: ${({ theme }) => theme.colors.shadow};
  backdrop-filter: blur(14px);
  overflow: hidden;
`;

const PreviewGlow = styled.div`
  position: absolute;
  inset: auto -90px -120px auto;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: rgba(45, 167, 183, 0.24);
  filter: blur(10px);
`;

const PreviewTitle = styled.h2`
  position: relative;
  z-index: 1;
  margin: 0 0 8px;
  font-size: 2rem;
  line-height: 1.05;
`;

const PreviewText = styled.p`
  position: relative;
  z-index: 1;
  margin: 0 0 22px;
  color: ${({ theme }) => theme.colors.muted};
`;

const PreviewFrame = styled.div`
  position: relative;
  z-index: 1;
  padding: 18px;
  border-radius: 24px;
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.colors.previewFrameStart} 0%,
    ${({ theme }) => theme.colors.previewFrameEnd} 100%
  );
  border: 1px solid ${({ theme }) => theme.colors.previewFrameBorder};
`;

const PreviewImage = styled.img`
  display: block;
  width: 100%;
  max-width: 320px;
  margin: 0 auto;
  filter: drop-shadow(0 16px 28px rgba(31, 41, 55, 0.12));
`;

const Section = styled.section`
  padding: 12px 0 32px;
`;

const FeatureGrid = styled.ul`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
  margin: 0;
  padding: 0;
  list-style: none;

  @media only screen and (max-width: ${({ theme }) => theme.mobileBreakpoint}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media only screen and (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.li`
  display: flex;
  flex-direction: column;
  border-radius: 26px;
  background: ${({ theme }) => theme.colors.cardSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.colors.cardShadow};
  overflow: hidden;
`;

const FeatureFigure = styled.div`
  margin: 0;
  padding: 18px 18px 0;
`;

const FeatureImage = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 554 / 1200;
  object-fit: cover;
  object-position: top;
  border-radius: 20px;
  border: 1px solid rgba(23, 49, 60, 0.1);
  background: ${({ theme }) => theme.colors.surfaceMuted};
`;

const FeatureBody = styled.div`
  padding: 22px 26px 26px;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const FeatureLabel = styled.div`
  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.accentDark};
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const FeatureTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 1.3rem;
  line-height: 1.15;
`;

const FeatureDescription = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.98rem;
  line-height: 1.75;
`;

const BottomPanel = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-top: 36px;
  padding: 28px;
  border-radius: 28px;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.bottomPanelStart} 0%,
    ${({ theme }) => theme.colors.bottomPanelEnd} 100%
  );
  color: #fff;
  box-shadow: ${({ theme }) => theme.colors.bottomPanelShadow};

  @media only screen and (max-width: ${({ theme }) => theme.mobileBreakpoint}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const BottomCopy = styled.div`
  max-width: 580px;
`;

const SupportEyebrow = styled.div`
  color: rgba(255, 255, 255, 0.96);
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const BottomTitle = styled.h2`
  margin: 12px 0 10px;
  font-size: clamp(1.8rem, 3vw, 2.8rem);
  line-height: 1.05;
  letter-spacing: -0.04em;
`;

const BottomText = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.96);
`;

const BottomActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
`;
